/**
 * Screen-space clustering for the sale dots.
 *
 * A liquid token prints hundreds of sales a day; drawn one dot each they
 * become a smear that hides the candles and answers nothing. Dots within a
 * few pixels of each other are merged into one, sized by how many it holds,
 * so a dense hour reads as one heavy mark and a lone grail as one light one.
 *
 * Greedy and stable: points are taken in x order and each joins the first
 * cluster whose centroid is within `radius` on both axes, or starts its own.
 * The centroid is the running mean, so a cluster settles where its members
 * are rather than at whichever came first. Because the points are sorted, a
 * cluster whose centroid has fallen more than `radius` behind the current x
 * can take no more members and is skipped.
 */

export interface ClusterPoint {
	x: number
	y: number
}

export interface Cluster<T extends ClusterPoint> {
	x: number
	y: number
	items: T[]
}

export function clusterPoints<T extends ClusterPoint>(points: readonly T[], radius: number): Cluster<T>[] {
	const sorted = [...points].sort((a, b) => a.x - b.x)
	const clusters: Cluster<T>[] = []
	// Index of the oldest cluster that can still accept a point.
	let live = 0
	for (const p of sorted) {
		while (live < clusters.length && clusters[live].x < p.x - radius) {
			live += 1
		}
		let home: Cluster<T> | null = null
		for (let i = live; i < clusters.length; i += 1) {
			const c = clusters[i]
			if (Math.abs(c.x - p.x) <= radius && Math.abs(c.y - p.y) <= radius) {
				home = c
				break
			}
		}
		if (home) {
			const n = home.items.length
			home.x = (home.x * n + p.x) / (n + 1)
			home.y = (home.y * n + p.y) / (n + 1)
			home.items.push(p)
		} else {
			clusters.push({ x: p.x, y: p.y, items: [p] })
		}
	}
	return clusters
}

/**
 * Dot radius for a cluster: area grows with the count, so ten sales read as
 * about three times the mark of one, and it stops growing at a size that
 * still sits inside a candle.
 */
export function clusterRadius(count: number, base = 3, max = 10): number {
	return Math.min(max, base + 1.6 * Math.sqrt(Math.max(0, count - 1)))
}
